/**
 * Shop Integrations Service
 * Multi-tenant mağaza entegrasyonları için frontend servis
 */

import api from '@/lib/api';
import { t } from '@/i18n/t';

export type PlatformType = 'shopify' | 'woocommerce' | 'jtl' | 'ikas' | 'magento' | 'prestashop' | 'opencart';

export interface ShopIntegration {
  id: string;
  organization_id: string;
  platform_type: PlatformType;
  platform_name: string;
  description?: string;
  connection_config: Record<string, any>;
  is_active: boolean;
  is_default: boolean;
  last_test_at?: string;
  last_test_status?: 'success' | 'failed' | 'pending' | 'timeout' | 'unauthorized';
  last_test_error?: string;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  last_request_at?: string;
  created_at: string;
  updated_at: string;
  created_by_id?: string;
  created_by_name?: string;
}

export interface CreateShopIntegrationData {
  platformType: PlatformType;
  platformName: string;
  description?: string;
  connectionConfig: Record<string, any>;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface UpdateShopIntegrationData {
  platformName?: string;
  description?: string;
  connectionConfig?: Record<string, any>;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface ShopIntegrationsResponse {
  success: boolean;
  message: string;
  data: {
    integrations: ShopIntegration[];
    organizationId: string;
    meta: {
      total: number;
      active: number;
      platforms: string[];
    };
  };
}

export interface ShopIntegrationResponse {
  success: boolean;
  message: string;
  data: {
    integration: ShopIntegration;
  };
}

export interface TestResult {
  success: boolean;
  message: string;
  error?: string;
  responseTime?: number;
  statusCode?: number;
  shopName?: string;
  shopDomain?: string;
}

export interface TestResponse {
  success: boolean;
  message: string;
  data: {
    testResult: TestResult;
    integrationId: string;
    testedAt: string;
  };
}

export interface PlatformConfigField {
  name: string;
  label: string;
  type: 'text' | 'password' | 'url' | 'number';
  required: boolean;
  default?: string;
  placeholder?: string;
  description?: string;
}

export interface SupportedPlatform {
  type: PlatformType;
  name: string;
  description: string;
  configFields: PlatformConfigField[];
  testable: boolean;
  icon?: string;
}

export interface PlatformsResponse {
  success: boolean;
  message: string;
  data: {
    platforms: SupportedPlatform[];
    total: number;
  };
}

class ShopIntegrationsService {
  /**
   * Organizasyonun tüm shop integration'larını getirir
   */
  async getShopIntegrations(organizationId: string): Promise<ShopIntegrationsResponse> {
    const response = await api.get<ShopIntegrationsResponse>(`/shop-integrations/${organizationId}`);
    return response.data;
  }

  /**
   * Yeni shop integration oluşturur
   */
  async createShopIntegration(organizationId: string, data: CreateShopIntegrationData): Promise<ShopIntegrationResponse> {
    const response = await api.post<ShopIntegrationResponse>(`/shop-integrations/${organizationId}`, data);
    return response.data;
  }

  /**
   * Shop integration günceller
   */
  async updateShopIntegration(id: string, data: UpdateShopIntegrationData): Promise<ShopIntegrationResponse> {
    const response = await api.put<ShopIntegrationResponse>(`/shop-integrations/${id}`, data);
    return response.data;
  }

  /**
   * Shop integration siler
   */
  async deleteShopIntegration(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/shop-integrations/${id}`);
    return response.data;
  }

  /**
   * Shop integration bağlantısını test eder
   */
  async testShopIntegration(id: string): Promise<TestResponse> {
    const response = await api.post<TestResponse>(`/shop-integrations/${id}/test`);
    return response.data;
  }

  /**
   * Desteklenen platformları getirir
   */
  async getSupportedPlatforms(): Promise<PlatformsResponse> {
    const response = await api.get<PlatformsResponse>('/shop-integrations/platforms/list');
    return response.data;
  }

  /**
   * Platform türüne göre varsayılan konfigürasyon döndürür
   */
  getDefaultConfig(platformType: PlatformType): Record<string, any> {
    switch (platformType) {
      case 'shopify':
        return {
          shop_url: '',
          access_token: '',
          api_version: '2023-10',
          webhook_secret: ''
        };
      case 'woocommerce':
        return {
          site_url: '',
          consumer_key: '',
          consumer_secret: '',
          api_version: 'wc/v3'
        };
      case 'jtl':
        return {
          jtl_url: '',
          api_key: '',
          client_id: ''
        };
      case 'ikas':
        return {
          api_url: '',
          api_key: '',
          store_id: ''
        };
      case 'magento':
        return {
          base_url: '',
          access_token: '',
          api_version: 'V1'
        };
      default:
        return {};
    }
  }

  /**
   * Platform adına göre ikon döndürür
   */
  getPlatformIcon(platformType: PlatformType): string {
    switch (platformType) {
      case 'shopify':
        return '🛍️';
      case 'woocommerce':
        return '🛒';
      case 'jtl':
        return '📦';
      case 'ikas':
        return '🏪';
      case 'magento':
        return '🔧';
      case 'prestashop':
        return '🏬';
      case 'opencart':
        return '🛫';
      default:
        return '🏪';
    }
  }

  /**
   * Platform displayname döndürür
   */
  getPlatformDisplayName(platformType: PlatformType): string {
    switch (platformType) {
      case 'shopify':
        return 'Shopify';
      case 'woocommerce':
        return 'WooCommerce';
      case 'jtl':
        return 'JTL Shop';
      case 'ikas':
        return 'İkas';
      case 'magento':
        return 'Magento';
      case 'prestashop':
        return 'PrestaShop';
      case 'opencart':
        return 'OpenCart';
      default:
        return platformType;
    }
  }

  /**
   * Test sonucuna göre duruma uygun renk döndürür
   */
  getTestStatusColor(status?: string): string {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      case 'timeout':
        return 'text-orange-600 bg-orange-50';
      case 'unauthorized':
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  }

  /**
   * Test sonucuna göre açıklama döndürür
   */
  getTestStatusLabel(status?: string): string {
    switch (status) {
      case 'success':
        return 'Başarılı';
      case 'failed':
        return 'Başarısız';
      case 'pending':
        return 'Bekliyor';
      case 'timeout':
        return 'Zaman Aşımı';
      case 'unauthorized':
        return 'Yetkisiz';
      default:
        return 'Bilinmiyor';
    }
  }

  /**
   * Connection config'den güvenli alanları filtreler (frontend display için)
   */
  getSafeConnectionConfig(config: Record<string, any>): Record<string, any> {
    const safeConfig: Record<string, any> = {};
    
    for (const [key, value] of Object.entries(config)) {
      // Sensitive field'ları gizle
      if (key.includes('token') || key.includes('secret') || key.includes('key')) {
        safeConfig[key] = value && value !== '[HIDDEN]' ? '[HIDDEN]' : value;
      } else {
        safeConfig[key] = value;
      }
    }
    
    return safeConfig;
  }

  /**
   * Başarı oranını hesaplar
   */
  calculateSuccessRate(integration: ShopIntegration): number {
    if (integration.total_requests === 0) return 0;
    return Math.round((integration.successful_requests / integration.total_requests) * 100);
  }

  /**
   * Son işlem zamanını formatlar
   */
  formatLastRequestTime(lastRequestAt?: string): string {
    if (!lastRequestAt) return t('errors:fix.neverUsed', 'Noch nie verwendet');
    
    const date = new Date(lastRequestAt);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return t('errors:fix.justNow', 'Gerade eben');
    if (diffInMinutes < 60) return t('errors:fix.minutesAgo', 'vor {{count}} Minuten', { count: diffInMinutes });
    if (diffInMinutes < 1440) return t('errors:fix.hoursAgo', 'vor {{count}} Stunden', { count: Math.floor(diffInMinutes / 60) });
    return t('errors:fix.daysAgo', 'vor {{count}} Tagen', { count: Math.floor(diffInMinutes / 1440) });
  }

  /**
   * Connection config validasyonu
   */
  validateConnectionConfig(platformType: PlatformType, config: Record<string, any>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    switch (platformType) {
      case 'shopify':
        if (!config.shop_url) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'Shop URL' }));
        if (!config.access_token) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'Access Token' }));
        if (!config.api_version) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'API Version' }));
        if (config.shop_url && !config.shop_url.includes('.myshopify.com')) {
          errors.push('Shop URL .myshopify.com domain\'i içermelidir');
        }
        break;
        
      case 'woocommerce':
        if (!config.site_url) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'Site URL' }));
        if (!config.consumer_key) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'Consumer Key' }));
        if (!config.consumer_secret) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'Consumer Secret' }));
        break;
        
      case 'jtl':
        if (!config.jtl_url) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'JTL URL' }));
        if (!config.api_key) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'API Key' }));
        break;
        
      case 'ikas':
        if (!config.api_url) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'API URL' }));
        if (!config.api_key) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'API Key' }));
        if (!config.store_id) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'Store ID' }));
        break;
        
      case 'magento':
        if (!config.base_url) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'Base URL' }));
        if (!config.access_token) errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: 'Access Token' }));
        break;
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default new ShopIntegrationsService();